import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import type { EpaQuestions, PowerPoleQuestions, NewJobQuoteItemExtra } from './models/JobInterfaces';
import type { GenericList, ItemOptionList } from './models/JobSharedInterfaces';
import NewJobPricelistPowerPoleQuestionsData from './NewJobPricelistPowerPoleQuestions';
import JobQuoteEpaQuestionsData from './JobQuoteEpaQuestions';
import { GetQuery, PostQuery } from './Shared';

interface RunSheetEditJobProps {
    showModal: boolean;
    systemCompanyId: number;
    scheduleId: number;
    fromRunsheet: boolean;      //We don't want to update the quote when editing from the runsheet
    hideModal(): void;
    saveModal(quantityScheduled: number, quantityTotal: number, item: string, itemShortcut: string, rowVersionTimestamp: Uint8Array): void;
}

interface EditJobItem {
    id: number;
    companyId: number;
    itemId: number;
    item: string;
    invoiceDescription: string;
    itemTypeId: number;
    productGroupId: number;
    typeId: number;         //Install = 1, Pickup = 2
    hirePeriod: number;
    defaultHirePeriod: number;
    customHirePeriod: boolean;
    frequencyId: number;
    locationId: number;
    isEws: boolean;
    isEvent: boolean;
    quantityTotal: number;
    quantityScheduled: number;
    priceIsZero: boolean;   //for pump & cleans associated with monthly maint toilets
    extraHours: number;
    size: number;
    initial: number;
    travel: number;
    cleaningFee: number;
    trenchFee: number;
    fuelSurchargeAmount: number;
    confinedSpaceAmount: number;
    sampleTestingAmount: number;
    wasteDisposalAmount: number;
    price: number;
    overhireRate: number;
    reason: string;
    isKwikstage: boolean;
    scheduleKwikstageItems: KwikstageScheduleItem[];
    canEditOverhire: boolean;
    trolley: boolean;
    tables: number;
    chairs: number;
    extras: NewJobQuoteItemExtra;
    invoicePeriodTypeId: number;    //1 = Weeks, 2 = Months
    installOnly: boolean;
    isAllocated: boolean;
}

interface KwikstageScheduleItem {
    scheduleKwikstageId: number;
    starterBayId: string;
    liftId: string;
    baysTotal: number;
    bays: number;
}

const initEditJob: EditJobItem = {
    id: 0,
    companyId: 0,
    itemId: 0,
    item: "",
    invoiceDescription: "",
    itemTypeId: 0,
    productGroupId: 0,
    typeId: 0,
    hirePeriod: 0,
    defaultHirePeriod: 0,
    customHirePeriod: false,
    frequencyId: 0,
    locationId: -1,
    isEws: false,
    isEvent: false,
    quantityTotal: 0,
    quantityScheduled: 0,
    trolley: false,
    priceIsZero: false,
    extraHours: 0,
    size: 0,
    initial: 0,
    travel: 0,
    cleaningFee: 0,
    trenchFee: 0,
    fuelSurchargeAmount: 0,
    confinedSpaceAmount: 0,
    sampleTestingAmount: 0,
    wasteDisposalAmount: 0,
    price: 0,
    overhireRate: 0,
    reason: "",
    isKwikstage: false,
    scheduleKwikstageItems: [],
    canEditOverhire: false,
    tables: 0,
    chairs: 0,
    extras: {
        itemId: 0,
        quantity: 0,
        equipped: false
    },
    invoicePeriodTypeId: 1,
    installOnly: false,
    isAllocated: false
};

const initPowerPoleQuestions: PowerPoleQuestions = {
    isUnderground: false,
    superSafeConnecting: -1,
    superSafeSupplierId: -1,
    powerPoleLocationId: -1,
    powerPreviouslyConnected: -1,
    powerAbolished: -1,
    siteLocked: -1,
    clearSightLine: -1,
    pitOnSite: -1,
    pitDistanceId: -1,
    superSafeDiggingTrench: -1
};

const RunsheetEditJobPopup = (props: RunSheetEditJobProps) => {
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [gateCode, setGateCode] = React.useState("");
    const [itemQuestions, setItemQuestions] = React.useState({ showQuestions: false, itemType: 0 }); //itemtype: 1 Powerpoles, 2 EPA
    const [itemOptions, setItemOptions] = React.useState<ItemOptionList[]>([]);
    const [extraItemOptions, setExtraItemOptions] = React.useState<GenericList[]>([]);
    const [frequencyOptions, setFrequencyOptions] = React.useState<GenericList[]>([]);
    const [yesNoOptions, setYesNoOptions] = React.useState<GenericList[]>([]);
    const [superSafeSupplierOptions, setSuperSafeSupplierOptions] = React.useState<GenericList[]>([]);
    const [powerPoleLocationOptions, setPowerPoleLocationOptions] = React.useState<GenericList[]>([]);
    const [powerPoleDistanceOptions, setPowerPoleDistanceOptions] = React.useState<GenericList[]>([]);
    const [editJobItem, setEditJobItem] = React.useState<EditJobItem>(initEditJob);
    const [powerPoleQuestions, setPowerPoleQuestions] = React.useState<PowerPoleQuestions>(initPowerPoleQuestions);
    const [invoiceAmount, setInvoiceAmount] = React.useState(0);
    const [invoiceAmountFormatted, setInvoiceAmountFormatted] = React.useState("$0.00");
    const [invoicePcRemaining, setInvoicePcRemaining] = React.useState(0);
    const [invoicePcExtra, setInvoicePcExtra] = React.useState(0);
    const [epaItemQuestions, setEpaItemQuestions] = React.useState<EpaQuestions>({
        tankLidExposed: -1,
        tankLidLocationKnown: -1,
        tankLidLocation: "",
        truckLargeAccess: -1,
        truckSmallAccess: -1,
        truckAccessToTank: -1,  //within 25m
        steepDriveway: -1,
        hoseMetres: 0,
        sealedDriveway: -1,
        lastService: "",
        waterBoard: "",
        waterAccessOnSite: -1,
        emergencySameDayJob: -1,
        weekendJob: -1,
        siteVisit: -1,
        confinedSpace: -1,
        materialSafetyDataSheet: -1,
        sampleTestingRequired: -1
    });
    const [epaLidLocationFeeFormatted, setEpaLidLocationFeeFormatted] = React.useState("$0.00");
    const [confinedSpaceFee, setConfinedSpaceFee] = React.useState({ fee: 0, feeFormatted: "$0.00" });
    const [weekendFee, setWeekendFee] = React.useState({ fee: 0, feeFormatted: "$0.00" });
    const [emergencyFee, setEmergencyFee] = React.useState({ fee: 0, feeFormatted: "$0.00" });
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [calcPrice, setCalcPrice] = React.useState(false);
    const [calcJobPrice, setCalcJobPrice] = React.useState(false);  //totals amounts to get total price

    React.useEffect(() => {
        if (props.scheduleId > 0 && props.showModal) {
            getData(props.scheduleId);
        }
    }, [props.scheduleId]);

    const getData = async (scheduleId: number) => {
        const apiUrl = 'GetEditJobDetails?ScheduleId=' + scheduleId + "&SystemCompanyId=" + props.systemCompanyId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setGateCode(result.data.gateCode);
            var pp = result.data.editJobDetails.itemTypeId === 23;
            var epa = result.data.editJobDetails.productGroupId === 6;
            setItemQuestions({ showQuestions: result.data.showItemQuestions, itemType: pp ? 1 : epa ? 2 : 0 });
            setItemOptions(result.data.itemOptions);
            setExtraItemOptions(result.data.extraItemOptions);
            setFrequencyOptions(result.data.frequencyOptions);
            setYesNoOptions(result.data.yesNoOptions);
            setSuperSafeSupplierOptions(result.data.superSafeSupplierOptions);
            setPowerPoleLocationOptions(result.data.powerPoleLocationOptions);
            setPowerPoleDistanceOptions(result.data.powerPoleDistanceOptions);
            setEpaLidLocationFeeFormatted(result.data.epaLidLocationFeeFormatted);
            setConfinedSpaceFee({ fee: result.data.confinedSpaceFee, feeFormatted: result.data.confinedSpaceFeeFormatted });
            setWeekendFee({ fee: result.data.weekendFee, feeFormatted: result.data.weekendFeeFormatted });
            setEmergencyFee({ fee: result.data.emergencyFee, feeFormatted: result.data.emergencyFeeFormatted });
            setEditJobItem(result.data.editJobDetails);
            setPowerPoleQuestions(result.data.powerPoleQuestions);
            setEpaItemQuestions(result.data.epaQuestions);
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if (calcPrice) {
            calculateEditJobPrice(editJobItem);
        }
    }, [calcPrice]);

    React.useEffect(() => {
        if (calcJobPrice) {
            calculateJobPrice();
        }
    }, [calcJobPrice]);

    const resetScreenData = () => {
        setErrors({});
        setGateCode("");
        setSaveDisabled(false);
        setItemQuestions({ showQuestions: false, itemType: 0 });
        let resetEditJobItem: EditJobItem = {
            id: 0,
            companyId: 0,
            itemId: 0,
            item: "",
            invoiceDescription: "",
            itemTypeId: 0,
            productGroupId: 0,
            typeId: 0,
            hirePeriod: 0,
            defaultHirePeriod: 0,
            customHirePeriod: false,
            frequencyId: 0,
            locationId: -1,
            isEws: false,
            isEvent: false,
            quantityTotal: 0,
            quantityScheduled: 0,
            trolley: false,
            priceIsZero: false,
            extraHours: 0,
            size: 0,
            initial: 0,
            travel: 0,
            cleaningFee: 0,
            trenchFee: 0,
            fuelSurchargeAmount: 0,
            confinedSpaceAmount: 0,
            sampleTestingAmount: 0,
            wasteDisposalAmount: 0,
            price: 0,
            overhireRate: 0,
            reason: "",
            isKwikstage: false,
            scheduleKwikstageItems: [],
            canEditOverhire: false,
            tables: 0,
            chairs: 0,
            extras: {
                itemId: 0,
                quantity: 0,
                equipped: false
            },
            invoicePeriodTypeId: 1,
            installOnly: false,
            isAllocated: false
        };
        setEditJobItem(resetEditJobItem);
        let resetPowerPoleQuestions: PowerPoleQuestions = {
            isUnderground: false,
            superSafeConnecting: -1,
            superSafeSupplierId: -1,
            powerPoleLocationId: -1,
            powerPreviouslyConnected: -1,
            powerAbolished: -1,
            siteLocked: -1,
            clearSightLine: -1,
            pitOnSite: -1,
            pitDistanceId: -1,
            superSafeDiggingTrench: -1
        };
        setPowerPoleQuestions(resetPowerPoleQuestions);
        resetEpaQuestions();
        setInvoiceAmount(0);
        setInvoiceAmountFormatted("$0.00");
        setInvoicePcRemaining(0);
    }

    const resetEpaQuestions = () => {
        setEpaItemQuestions(prevState => ({
            ...prevState,
            tankLidExposed: -1,
            tankLidLocationKnown: -1,
            tankLidLocation: "",
            truckLargeAccess: -1,
            truckSmallAccess: -1,
            truckAccessToTank: -1,  //within 25m
            steepDriveway: -1,
            hoseMetres: 0,
            sealedDriveway: -1,
            lastService: "",
            waterBoard: "",
            waterAccessOnSite: -1,
            emergencySameDayJob: -1,
            weekendJob: -1
        }));
    }

    const handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        deleteError(e.target.name);

        if (e.target.name === "price") {
            //reset initial price
            var initialPrice = e.target.value - editJobItem.travel - editJobItem.cleaningFee - editJobItem.trenchFee - editJobItem.confinedSpaceAmount - editJobItem.sampleTestingAmount - editJobItem.wasteDisposalAmount - editJobItem.fuelSurchargeAmount;
            setEditJobItem(prevState => ({ ...prevState, initial: initialPrice }));
        }

        setEditJobItem(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const deleteError = (itemName: string) => {
        var errorList = errors;
        if (itemName) {
            delete errorList[itemName];
        }
        setErrors(errorList);
    }

    const handlePriceChange = (e: any) => {
        deleteError(e.target.name);
        var value = e.target.value * 1;
        setEditJobItem(prevState => ({ ...prevState, [e.target.name]: value }));

        setCalcJobPrice(true);
    }

    const handleItemIdChange = (e: any) => {
        deleteError(e.target.name);

        var value = e.target.value * 1;
        setEditJobItem(prevState => ({ ...prevState, [e.target.name]: value }));

        var itemOption = itemOptions.find(i => i.id === value);
        if (itemOption) {
            //update default hire period days
            let hirePeriod = itemOption.priceListDefaultDays === 0 ? 1 : itemOption.priceListDefaultDays;
            let defaultHirePeriod = itemOption.priceListDefaultDays;
            let invPerId = itemOption.invoicePeriodTypeId;
            let installOnly = itemOption.installOnly;
            let itemName = itemOption.name;
            let size = 0;
            //Septics, Grease Traps and Waste Tank default sizes
            if (editJobItem.itemTypeId === 58) {
                size = itemOption.name.includes("Treatment Plant") ? 7000 : 3200;
            } else if (editJobItem.itemTypeId === 59) {
                size = 1000;
            } else if (editJobItem.itemTypeId === 60) {
                size = 4000;
            }
            setEditJobItem(prevState => ({ ...prevState, item: itemName, hirePeriod: hirePeriod, installOnly: installOnly, customHirePeriod: false, defaultHirePeriod: defaultHirePeriod, frequencyId: -1, locationId: -1, size: size, invoicePeriodTypeId: invPerId }));

            if (itemOption.typeId === 23) {
                //power pole - check if it is underground
                //make sure to reset the questions that don't apply
                if (itemName.toLowerCase().includes("underground")) {
                    setPowerPoleQuestions(prevState => ({ ...prevState, isUnderground: true, clearSightLine: -1 }));
                    calculatePowerPolePrice(value, editJobItem.customHirePeriod, editJobItem.hirePeriod, editJobItem.quantityTotal, powerPoleQuestions.superSafeSupplierId, powerPoleQuestions.pitDistanceId, powerPoleQuestions.superSafeDiggingTrench);
                } else {
                    setPowerPoleQuestions(prevState => ({ ...prevState, isUnderground: false, pitOnSite: -1, pitDistanceId: -1, superSafeDiggingTrench: -1 }));
                    calculatePowerPolePrice(value, editJobItem.customHirePeriod, editJobItem.hirePeriod, editJobItem.quantityTotal, powerPoleQuestions.superSafeSupplierId, -1, -1);
                }
            } else {
                setCalcPrice(true);
            }
        }
    }

    const handleFrequencyIdChange = (e: any) => {
        deleteError(e.target.name);

        var value = e.target.value * 1;

        setEditJobItem(prevState => ({ ...prevState, [e.target.name]: value }));

        setCalcPrice(true);
    }

    const handleLocationIdChange = (e: any) => {
        deleteError(e.target.name);

        var value = e.target.value * 1;

        setEditJobItem(prevState => ({ ...prevState, [e.target.name]: value }));

        setCalcPrice(true);
    }

    const handleHirePeriodChange = (e: any) => {
        deleteError(e.target.name);

        var value = e.target.value * 1;

        setEditJobItem(prevState => ({ ...prevState, [e.target.name]: value }));
        
        if (editJobItem.itemTypeId === 23) {
            //power pole so need to use the power pole calculation
            calculatePowerPolePrice(editJobItem.itemId, editJobItem.customHirePeriod, value, editJobItem.quantityTotal, powerPoleQuestions.superSafeSupplierId, powerPoleQuestions.pitDistanceId, powerPoleQuestions.superSafeDiggingTrench);
        } else {
            setCalcPrice(true);
        }
    }

    const handleQuantityTotalChange = (e: any) => {
        deleteError(e.target.name);

        var value = e.target.value * 1;
        setEditJobItem(prevState => ({ ...prevState, [e.target.name]: value }));

        if (props.systemCompanyId === 3) {
            //need to update the scheduled quantity as well
            setEditJobItem(prevState => ({ ...prevState, quantityScheduled: value }));
        }

        if (editJobItem.itemTypeId === 23) {
            //power pole so need to use the power pole calculation
            calculatePowerPolePrice(editJobItem.itemId, editJobItem.customHirePeriod, editJobItem.hirePeriod, value, powerPoleQuestions.superSafeSupplierId, powerPoleQuestions.pitDistanceId, powerPoleQuestions.superSafeDiggingTrench);
        } else if (editJobItem.itemTypeId != 53) {
            //re-calc for everything except Silt Barriers - they use quoted price as it is just one price
            setCalcPrice(true);
        }
    }

    const editCustomHirePeriod = (e: any) => {
        e.preventDefault();
        let updatedHirePeriod = editJobItem.invoicePeriodTypeId === 2 ? editJobItem.hirePeriod : editJobItem.hirePeriod / 7;
        setEditJobItem(prevState => ({ ...prevState, customHirePeriod: true, hirePeriod: updatedHirePeriod }));
    }

    const handleTrolleyChange = (e: any) => {
        deleteError(e.target.name);

        setEditJobItem(prevState => ({ ...prevState, trolley: e.target.checked }));
        setCalcPrice(true);
    }

    const handleCrossoverQtyChange = (e: any) => {
        deleteError(e.target.name);

        var value = e.target.value * 1;
        var extras = editJobItem.extras;
        let updatedExtras: any = {};
        updatedExtras = extras;
        updatedExtras.itemId = value === 0 ? 0 : editJobItem.itemId;
        updatedExtras.quantity = value;
        setEditJobItem(prevState => ({
            ...prevState,
            extras: updatedExtras
        }));
       
        setCalcPrice(true);
    }

    const handleExtrasQtyChange = (e: any) => {
        deleteError(e.target.name);

        var value = e.target.value * 1;
        var extras = editJobItem.extras;
        let updatedExtras: any = {};
        updatedExtras = extras;
        updatedExtras.quantity = value;

        setEditJobItem(prevState => ({
            ...prevState,
            extras: updatedExtras 
        }));

        setCalcPrice(true);
    }

    const handleKwikstageChange = (e: any, index: number) => {
        deleteError(e.target.name);

        var kwikstage = [...editJobItem.scheduleKwikstageItems];
        var kwikstateItem = kwikstage[index];
        let newJobItem: any = {};
        newJobItem = kwikstateItem;
        newJobItem[e.target.name] = e.target.value;

        calculateKsPrice(editJobItem.id, kwikstage);
    }

    const calculateKsPrice = async (scheduleId: number, ksItems: KwikstageScheduleItem[]) => {
        var calcKsPrice = {
            scheduleId: scheduleId,
            ksItems: ksItems
        };

        var jsonItem = JSON.stringify(calcKsPrice);

        const apiUrl = "CalculatePlanChangesKwikstagePrice";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.amount > 0) {
                //only update the quote prices if there is a pricelist amount
                setEditJobItem(prevState => ({
                    ...prevState,
                    price: result.data.amount,
                    travel: result.data.travel,
                    initial: result.data.initial,
                    overhireRate: result.data.overhireRate,
                    scheduleKwikstageItems: ksItems
                }));
            }
        }
    }

    const calculateEditJobPrice = async (editJob: EditJobItem) => {
        var hirePeriod = editJob.customHirePeriod && editJob.invoicePeriodTypeId === 1 ? editJob.hirePeriod * 7 : editJob.hirePeriod;
        var chargeWeekendFee = epaItemQuestions.weekendJob === 1;
        var chargeEmergencyFee = epaItemQuestions.emergencySameDayJob === 1;

        const apiUrl = 'CalculateEditJobPrice?CompanyId=' + editJob.companyId + '&ScheduleId=' + editJob.id + '&ItemId=' + editJob.itemId + '&HirePeriod=' + hirePeriod + '&QuantityTotal=' + editJob.quantityTotal + '&FrequencyId=' + editJob.frequencyId + '&LocationId=' + editJob.locationId + '&Trolley=' + editJob.trolley + '&ExtraHours=' + editJob.extraHours + '&Size=' + editJob.size + '&ExtraItemId=' + editJob.extras.itemId + '&ExtraItemQuantity=' + editJob.extras.quantity + '&ChargeWeekendJob=' + chargeWeekendFee + '&ChargeEmergencyJob=' + chargeEmergencyFee;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setCalcPrice(false);
            let newPrices = result.data.itemNewPrice;
            setEditJobItem(prevState => ({ ...prevState, initial: newPrices.initialHire, travel: newPrices.travel, cleaningFee: newPrices.cleaning, fuelSurchargeAmount: newPrices.fuelSurcharge, price: newPrices.amount, overhireRate: newPrices.overhireRate }));
            let pcInvoicePrice = result.data.pumpCleanInvoiceDetails;
            setInvoiceAmount(pcInvoicePrice.amount);
            setInvoiceAmountFormatted(pcInvoicePrice.amountFormatted);
            setInvoicePcRemaining(pcInvoicePrice.pumpAndCleanRemaining);
            setInvoicePcExtra(pcInvoicePrice.pumpAndCleanExtra);
        }
    }

    const calculateJobPrice = () => {
        //total all fields and set total price
        let itemAmount = editJobItem.initial + editJobItem.travel + editJobItem.cleaningFee + editJobItem.trenchFee + editJobItem.fuelSurchargeAmount + editJobItem.confinedSpaceAmount + editJobItem.sampleTestingAmount + editJobItem.wasteDisposalAmount;
        let jobPrice = parseFloat(itemAmount.toFixed(2));
        setEditJobItem(prevState => ({ ...prevState, price: jobPrice }));
        setCalcJobPrice(false);
    }

    const hideModal = () => {
        resetScreenData();
        props.hideModal();
    }

    const updateGateCode = (updatedGateCode: string) => {
        setGateCode(updatedGateCode);
    }

    const updatePowerPoleQuestions = (ppQuestions: PowerPoleQuestions) => {
        setPowerPoleQuestions(prevState => ({
            ...prevState,
            isUnderground: ppQuestions.isUnderground,
            superSafeConnecting: ppQuestions.superSafeConnecting,
            superSafeSupplierId: ppQuestions.superSafeSupplierId,
            powerPoleLocationId: ppQuestions.powerPoleLocationId,
            powerPreviouslyConnected: ppQuestions.powerPreviouslyConnected,
            powerAbolished: ppQuestions.powerAbolished,
            siteLocked: ppQuestions.siteLocked,
            clearSightLine: ppQuestions.clearSightLine,
            pitOnSite: ppQuestions.pitOnSite,
            pitDistanceId: ppQuestions.pitDistanceId,
            superSafeDiggingTrench: ppQuestions.superSafeDiggingTrench
        }));
        //calculate price
        calculatePowerPolePrice(editJobItem.itemId, editJobItem.customHirePeriod, editJobItem.hirePeriod, editJobItem.quantityTotal, ppQuestions.superSafeSupplierId, ppQuestions.pitDistanceId, ppQuestions.superSafeDiggingTrench );
    }

    const calculatePowerPolePrice = async (calcItemId: number, calcCustomHirePeriod: boolean, calcHirePeriod: number, calcQuantity: number, calcSuperSafeSupplierId: number, calcPitDistanceId: number, calcSuperSafeDiggingTrench: number) => {
        var calcSsDiggingTrench = calcSuperSafeDiggingTrench === 1;
        var hirePeriod = calcCustomHirePeriod ? calcHirePeriod * 7 : calcHirePeriod;

        const apiUrl = 'CalculateEditJobPowerPolePrice?CompanyId=' + props.systemCompanyId + '&ScheduleId=' + editJobItem.id + "&ItemId=" + calcItemId + "&HirePeriod=" + hirePeriod + "&Quantity=" + calcQuantity + "&PowerPoleSupplierId=" + calcSuperSafeSupplierId + "&PowerPoleDistanceId=" + calcPitDistanceId + "&PowerPoleSSDiggingTrench=" + calcSsDiggingTrench;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setEditJobItem(prevState => ({ ...prevState, initial: result.data.initialHire, trave: result.data.travel, cleaningFee: result.data.cleaning, trenchFee: result.data.trenchFee, fuelSurchargeAmount: result.data.fuelSurcharge, price: result.data.amount, overhireRate: result.data.overhireRate }));
        }
    }

    const handleEpaQuestionChange = (fieldName: string, fieldValue: number) => {
        setEpaItemQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));

        if (fieldName === "truckAccessToTank" || fieldName === "steepDriveway") {
            setEpaItemQuestions(prevState => ({ ...prevState, hoseMetres: 0 }));
        } else if (fieldName === "tankLidLocationKnown") {
            setEpaItemQuestions(prevState => ({ ...prevState, tankLidLocation: "" }));
        } else if (fieldName === "weekendJob") {
            setEpaItemQuestions(prevState => ({ ...prevState, emergencySameDayJob: -1 }));
        } else if (fieldName === "tankLidExposed") {
            setEpaItemQuestions(prevState => ({ ...prevState, tankLidLocationKnown: -1, tankLidLocation: "" }));
        }
    }
    
    const handleEpaQuestionTextChange = (fieldName: string, fieldValue: string) => {
        setEpaItemQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const handleEpaInvoiceDescriptionChange = (_updatedInvoiceDescription: string) => {
        //not used
    }

    const handleIncludeGreaseTrapChange = (updatedIncGreaseTrap: boolean) => {
        setEditJobItem(prevState => ({ ...prevState, trolley: updatedIncGreaseTrap }));
    }

    const updateSaveDisabled = (saveDisUpdated: boolean) => {
        setSaveDisabled(saveDisUpdated);
    }

    const saveEpa = () => {
        if (!epaItemQuestions.tankLidExposed) {
            setEditJobItem(prevState => ({ ...prevState, extraHours: 0.5 }));
        } else {
            setEditJobItem(prevState => ({ ...prevState, extraHours: 0 }));
        }

        setItemQuestions({ showQuestions: false, itemType: 0 });
        setSaveDisabled(false);

        //calculate price
        if (editJobItem.itemTypeId === 61) {
            var calloutFee = epaItemQuestions.weekendJob === 1 ? weekendFee.fee : epaItemQuestions.emergencySameDayJob === 1 ? emergencyFee.fee : 0;
            var confinedSpace = epaItemQuestions.confinedSpace === 1 ? confinedSpaceFee.fee : 0;
            setEditJobItem(prevState => ({ ...prevState, cleaningFee: calloutFee, confinedSpaceAmount: confinedSpace }));
            setCalcJobPrice(true);
        } else {
            setCalcPrice(true);
        }
    }

    const hideQuestions = (e: any) => {
        e.preventDefault();

        if (validatePowerPoleQuestions()) {
            setItemQuestions({ showQuestions: false, itemType: 0 });
        } else {
            toast.error("Please answer all Power Pole Questions before saving!");
        }
    }

    const validatePowerPoleQuestions = () => {
        var errorList: { [key: string]: string } = {};
        var formIsValid = true;

        //power pole validation
        if (powerPoleQuestions.superSafeConnecting < 0) {
            formIsValid = false;
            errorList["superSafeConnecting"] = "Super Safe Connecting Electricity is required";
        }

        if (powerPoleQuestions.superSafeConnecting === 1 && powerPoleQuestions.superSafeSupplierId < 0) {
            formIsValid = false;
            errorList["superSafeSupplierId"] = "Distributor is required";
        }

        if (powerPoleQuestions.powerPoleLocationId < 0) {
            formIsValid = false;
            errorList["powerPoleLocationId"] = "Power Pole Location is required";
        }

        if (powerPoleQuestions.powerPreviouslyConnected < 0) {
            formIsValid = false;
            errorList["powerPreviouslyConnected"] = "Power to the property is required";
        }

        if (powerPoleQuestions.powerPreviouslyConnected === 1 && powerPoleQuestions.powerAbolished < 0) {
            formIsValid = false;
            errorList["powerAbolished"] = "Power Abolished is required";
        }

        if (powerPoleQuestions.siteLocked < 0) {
            formIsValid = false;
            errorList["siteLocked"] = "Is Site Locked is required";
        }

        if (!powerPoleQuestions.isUnderground && powerPoleQuestions.clearSightLine < 0) {
            formIsValid = false;
            errorList["clearSightLine"] = "Clear Line of Sight is required";
        }

        if (powerPoleQuestions.isUnderground) {
            if (powerPoleQuestions.pitOnSite < 0) {
                formIsValid = false;
                errorList["pitOnSite"] = "Pit on site is required";
            } else if (powerPoleQuestions.pitOnSite === 1 && powerPoleQuestions.pitDistanceId < 0) {
                formIsValid = false;
                errorList["pitDistanceId"] = "Distance between pit and pole is required";
            } else if (powerPoleQuestions.pitOnSite === 1 && powerPoleQuestions.superSafeDiggingTrench < 0) {
                formIsValid = false;
                errorList["superSafeDiggingTrench"] = "Super Safe digging trench is required";
            }
        }
        setErrors(errorList);

        return formIsValid;
    }

    const validateEditJob = () => {
        let fields = editJobItem;
        let errorList: any = {};
        let formIsValid = true;

        if (fields.isKwikstage) {
            var rowNumber = 0;
            var errorString = "Please enter the following values: ";
            var error = false;

            //check kwikstage quantities
            for (var i = 0; i < fields.scheduleKwikstageItems.length; i++) {
                rowNumber += 1;
                var newErrorString = "";
                var field = fields.scheduleKwikstageItems[i];

                let bays = field.baysTotal * 1;
                if (bays < 1) {
                    newErrorString += "Bays, ";
                    error = true;
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
            }
            if (error) {
                formIsValid = false;
                errorList["baysTotal"] = errorString;
            }
        } else {
            //check normal quantity
            let quantityTotal = fields.quantityTotal * 1;
            if (quantityTotal < 1) {
                formIsValid = false;
                errorList["editquantityTotal"] = "Quantity Total is required";
            }

            let quantity = fields.quantityScheduled * 1;
            if (quantity < 1) {
                formIsValid = false;
                errorList["editquantity"] = "Quantity Scheduled is required";
            }

            if (quantity > quantityTotal) {
                formIsValid = false;
                errorList["editquantity"] = "Quantity must be <= Quantity Total";
            }
        }

        if (props.systemCompanyId === 3) {
            if (fields.productGroupId === 6 && fields.size <= 0) {
                formIsValid = false;
                errorList["size"] = "Tank Size must be > 0";
            }

            //only validate for company 3 and some items
            if (fields.itemTypeId != 22 && fields.itemTypeId != 26 && fields.itemTypeId != 44 && fields.itemTypeId != 47 && !fields.isEvent) {
                if (fields.hirePeriod <= 0) {
                    formIsValid = false;
                    errorList["hirePeriod"] = "Hire Period must be > 0";
                }
            }
        }

        if (fields.frequencyId === 0 && fields.initial < 0.01 && fields.itemTypeId != 22 && fields.itemTypeId != 47) {
            formIsValid = false;
            errorList["initial"] = "Initial must be > 0";
        }

        if (fields.frequencyId === 0 && fields.travel < 0) {
            formIsValid = false;
            errorList["travel"] = "Travel cannot be < 0";
        }

        if (fields.cleaningFee < 0) {
            formIsValid = false;
            errorList["cleaningFee"] = "Cleaning Fee cannot be < 0";
        }

        if (!fields.priceIsZero && fields.price < 0.01 && fields.itemTypeId != 22 && fields.itemTypeId != 47) {
            formIsValid = false;
            errorList["editprice"] = "Price is required";
        }

        //Pump & Cleans, Events, Toilet Delivery / Pickup doesn't have overhire rate
        if (!fields.installOnly && fields.itemTypeId != 45 && fields.itemTypeId != 53 && !fields.isEvent) {
            if (fields.overhireRate < 0.01) {
                formIsValid = false;
                errorList["editoverhirerate"] = "Overhire Rate is required";
            }
        }

        if (!fields.reason) {
            formIsValid = false;
            errorList["editreason"] = "Reason for change is required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = () => {
        let powerPolesValid = true;
        if (editJobItem.itemTypeId === 23) {
            powerPolesValid = validatePowerPoleQuestions();
        }

        if (powerPolesValid) {
            if (validateEditJob()) {
                
                saveEditJob();
            } else {
                toast.error("Fix validation errors on screen before saving");
            }
        } else {
            setItemQuestions({ showQuestions: false, itemType: 0 });
            toast.error("Answer all power pole questions before saving!");
        }
    }

    const saveEditJob = async () => {
        if (editJobItem.customHirePeriod && editJobItem.invoicePeriodTypeId === 1) {
            //need to change from weeks to days for saving
            editJobItem.hirePeriod = editJobItem.hirePeriod * 7;
        }

        var job = {
            id: editJobItem.id,
            itemId: editJobItem.itemId,
            item: editJobItem.item,
            invoiceDescription: editJobItem.invoiceDescription,
            quantityTotal: editJobItem.quantityTotal,
            quantity: editJobItem.quantityScheduled,
            trolley: editJobItem.trolley,
            hirePeriod: editJobItem.hirePeriod,
            customHirePeriod: editJobItem.customHirePeriod,
            frequencyId: editJobItem.frequencyId,
            locationId: editJobItem.locationId,
            extraHours: editJobItem.extraHours,
            size: editJobItem.size,
            extraItemId: editJobItem.extras.itemId,
            extraItemQuantity: editJobItem.extras.quantity,
            price: editJobItem.price,
            travel: editJobItem.travel,
            cleaningFee: editJobItem.cleaningFee,
            trenchFee: editJobItem.trenchFee,
            confinedSpaceAmount: editJobItem.confinedSpaceAmount,
            sampleTestingAmount: editJobItem.sampleTestingAmount,
            wasteDisposalAmount: editJobItem.wasteDisposalAmount,
            fuelSurchargeAmount: editJobItem.fuelSurchargeAmount,
            overhireRate: editJobItem.overhireRate,
            reason: editJobItem.reason,
            scheduleKwikstageItems: editJobItem.scheduleKwikstageItems,
            powerPoleQuestions: powerPoleQuestions,
            epaQuestions: epaItemQuestions,
            gateCode: gateCode,
            invoiceAmount: invoiceAmount,
            pcRemaining: invoicePcRemaining,
            pcExtra: invoicePcExtra,
            fromRunsheet: props.fromRunsheet
        };

        var jsonItem = JSON.stringify(job);

        const apiUrl = "UpdateSchedule";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            resetScreenData();
            toast.success("Edit Job Saved");

            props.saveModal(result.data.quantityScheduled, result.data.quantityTotal, result.data.item, result.data.itemShortcut, result.data.rowVersionTimestamp);
        }
    }

    const ppQuestions = <NewJobPricelistPowerPoleQuestionsData isReadOnly={false} isUnderground={powerPoleQuestions.isUnderground} powerPoleQuestions={powerPoleQuestions} gateCode={gateCode} yesNoOptions={yesNoOptions} powerPoleLocationOptions={powerPoleLocationOptions} powerPoleDistanceOptions={powerPoleDistanceOptions} superSafeSupplierOptions={superSafeSupplierOptions} errors={errors} updateGateCode={updateGateCode} updatePowerPoleQuestions={updatePowerPoleQuestions} />
    const epaQuestions = <JobQuoteEpaQuestionsData isReadOnly={false} itemTypeId={editJobItem.itemTypeId} invoiceDescription={""} showInvoiceDescription={false} includeGreaseTrap={editJobItem.trolley} epaLidLocationFeeFormatted={epaLidLocationFeeFormatted} weekendFeeFormatted={weekendFee.feeFormatted} emergencyFeeFormatted={emergencyFee.feeFormatted} epaQuestions={epaItemQuestions} yesNoOptions={yesNoOptions} showBack={true} saveDisabled={saveDisabled} updateNumber={handleEpaQuestionChange} updateText={handleEpaQuestionTextChange} updateInvoiceDescription={handleEpaInvoiceDescriptionChange} updateIncGreaseTrap={handleIncludeGreaseTrapChange} updateSaveDisabled={updateSaveDisabled} save={saveEpa} ></JobQuoteEpaQuestionsData>

    const renderDetails = (

        <Modal size={itemQuestions.showQuestions ? "lg" : undefined} backdrop="static" show={props.showModal} onHide={hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>Edit {editJobItem.item}</Modal.Title>
                <button type="button" className="close" onClick={hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <div className={itemQuestions.showQuestions && itemQuestions.itemType === 1 ? "" : "hidden"}>
                        {ppQuestions}
                    </div>
                    <div className={itemQuestions.showQuestions && itemQuestions.itemType === 2 ? "" : "hidden"}>
                        {epaQuestions}
                    </div>
                    <div className={itemQuestions.showQuestions ? "hidden" : ""}>
                        <span className={!props.fromRunsheet && editJobItem.isAllocated ? "makeitred" : "hidden"}>Get Logistics to Unallocate Truck to Edit Quantity / Price</span>
                        <div className={props.systemCompanyId === 2 || itemOptions.length === 0 ? "hidden" : ""}>
                            <label className="input-group" htmlFor="itemId">
                                <span className="label">Item</span>
                                <select className="select modal-inputsize200" id="itemId" name="itemId" value={editJobItem.itemId} onChange={(e) => handleItemIdChange(e)} disabled={!props.fromRunsheet && editJobItem.isAllocated}>
                                    <option hidden defaultValue="-1"></option>
                                    {itemOptions.map(item =>
                                        <option key={item.id} value={item.id}> {item.name}</option>)
                                    };
                                </select>
                            </label>
                        </div>

                        <label className={editJobItem.itemTypeId === 61 ? "input-group" : "hidden"} htmlFor="invoiceDescription">
                            <span className="label">Invoice Description</span>
                            <input className="input" type="text" maxLength={30} id="invoiceDescription" name="invoiceDescription" value={editJobItem.invoiceDescription} onChange={(e) => handleChange(e)}></input>
                        </label>
                        
                        <label className={editJobItem.productGroupId === 6 ? "input-group" : "hidden"} htmlFor="size">
                            <span className="label">Tank Size</span>
                            <input type='number' min={0} step={1} className="input modal-inputsize200 textalignright" id="size" name="size" value={editJobItem.size} onChange={(e) => handleQuantityTotalChange(e)} />
                        </label>
                        <span className="label errors marginLeftLabel">{errors["size"]}</span>

                        <label className={editJobItem.itemId === 81 ? "input-group" : "hidden"} htmlFor="locationId">
                            <span className="label">Location</span>
                            <select className="jobschedule-newjob-extra--width" id="locationId" name="locationId" value={editJobItem.locationId} onChange={(e) => handleLocationIdChange(e)}>
                                <option defaultValue="-1"></option>
                                <option key={1} value={1}>In Yard</option>
                                <option key={2} value={2}>On Site</option>
                            </select>
                        </label>

                        <div className={!editJobItem.item.toLowerCase().includes("sewer connect") && (editJobItem.itemTypeId === 25 || editJobItem.itemTypeId === 26 || editJobItem.itemTypeId === 36 || (editJobItem.itemTypeId > 57 && editJobItem.itemTypeId < 61)) ? "" : "hidden"}>
                            <label className="input-group" htmlFor="frequencyId">
                                <span className="label">Frequency</span>
                                <select className="jobschedule-newjob-extra--width" id="frequencyId" name="frequencyId" value={editJobItem.frequencyId} onChange={(e) => handleFrequencyIdChange(e)}>
                                    <option defaultValue="-1"></option>
                                    {frequencyOptions.map(freq =>
                                        <option key={freq.id} hidden={(editJobItem.itemTypeId === 59 && (freq.id < 4 || freq.id > 8)) || (editJobItem.itemTypeId === 58 && freq.id < 9) || ((editJobItem.itemTypeId < 58 || editJobItem.itemTypeId > 59) && freq.id > 3)} value={freq.id}>{freq.name}</option>
                                    )};
                                </select>
                            </label>
                        </div>

                        <div className={props.systemCompanyId === 3 ? "" : "hidden"}>
                            <label className={editJobItem.installOnly || editJobItem.itemTypeId === 45 || editJobItem.isEvent ? "hidden" : "input-group"} htmlFor="hirePeriod">
                                <span className="label">Hire Period {editJobItem.invoicePeriodTypeId === 2 ? "(months)" : "(weeks)"}</span>
                                <select className={editJobItem.customHirePeriod ? "hidden" : "jobschedule-newjob-extra--width"} id="hirePeriod" name="hirePeriod" value={editJobItem.hirePeriod} onChange={(e) => handleHirePeriodChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    <option hidden={editJobItem.defaultHirePeriod > 1} value={1}>1 Day</option>
                                    <option hidden={editJobItem.defaultHirePeriod > 7 || editJobItem.invoicePeriodTypeId === 2} value={7}>1 Week</option>
                                    <option hidden={editJobItem.defaultHirePeriod > 14 || editJobItem.invoicePeriodTypeId === 2} value={14}>2 Weeks</option>
                                    <option hidden={editJobItem.defaultHirePeriod > 21 || editJobItem.invoicePeriodTypeId === 2} value={21}>3 Weeks</option>
                                    <option hidden={editJobItem.defaultHirePeriod > 28 || editJobItem.invoicePeriodTypeId === 2} value={28}>4 Weeks</option>
                                    <option hidden={editJobItem.defaultHirePeriod > 35 || editJobItem.invoicePeriodTypeId === 2} value={35}>5 Weeks</option>
                                    <option hidden={editJobItem.itemTypeId === 19 || editJobItem.itemTypeId === 23 || editJobItem.itemTypeId === 28 || editJobItem.itemTypeId === 27 || editJobItem.invoicePeriodTypeId === 2} value={42}>6 Weeks</option>
                                    <option hidden={editJobItem.itemTypeId === 23 || editJobItem.itemTypeId === 25 || editJobItem.itemTypeId == 26 || editJobItem.itemTypeId === 28 || editJobItem.itemTypeId === 27 || editJobItem.invoicePeriodTypeId === 2} value={56}>8 Weeks</option>
                                    <option hidden={editJobItem.itemTypeId === 19 || editJobItem.itemTypeId === 23 || editJobItem.itemTypeId === 28 || editJobItem.itemTypeId === 27 || editJobItem.invoicePeriodTypeId === 2} value={70}>10 Weeks</option>
                                    <option hidden={editJobItem.invoicePeriodTypeId === 2} value={91}>13 Weeks</option>
                                    <option hidden={editJobItem.itemTypeId === 23 || editJobItem.invoicePeriodTypeId === 2} value={98}>14 Weeks</option>
                                    <option hidden={editJobItem.itemTypeId === 23 || editJobItem.invoicePeriodTypeId === 2} value={112}>16 Weeks</option>
                                    <option value={editJobItem.invoicePeriodTypeId === 2 ? 6 : 182}>{editJobItem.invoicePeriodTypeId === 2 ? "6 Months" : "26 Weeks"}</option>
                                    <option hidden={editJobItem.itemTypeId != 25} value={280}>40 Weeks</option>
                                    <option value={editJobItem.invoicePeriodTypeId === 2 ? 12 : 364}>{editJobItem.invoicePeriodTypeId === 2 ? "12 Months" : "52 Weeks"}</option>
                                </select>
                                <input type='number' min="0" step="1" className={editJobItem.customHirePeriod ? "form-control modal-inputsize200 textalignright" : "hidden"} id="hirePeriod" name="hirePeriod" value={editJobItem.hirePeriod} onChange={(e) => handleHirePeriodChange(e)} />
                                <span className={editJobItem.customHirePeriod || editJobItem.itemId < 1 || editJobItem.isEvent ? "hidden" : "tooltipitem tooltipitem__right"}><i className="fa-solid fa-pen-to-square marginLeft10" onClick={(e) => editCustomHirePeriod(e)}></i>
                                    <span className="tooltipitemtext tooltipitemtext__right">Enter Hire Period in {editJobItem.invoicePeriodTypeId === 2 ? "Months" : "Weeks"}</span>
                                </span>
                            </label>
                            <span className={errors["hirePeriod"] ? "label errors marginLeftLabel" : "hidden"}>{errors["hirePeriod"]}</span>

                        </div>

                        <div className={editJobItem.typeId === 1 && (editJobItem.itemTypeId === 25 || editJobItem.itemTypeId === 29 || editJobItem.itemTypeId === 44 || editJobItem.itemTypeId === 58) ? "" : "hidden"}>
                            <label className="input-group" htmlFor="trolley">
                                <span className="label">{editJobItem.itemTypeId === 58 ? "Include Grease Trap?" : "Include Trolley?"}</span>
                                <input className="checkbox" type="checkbox" id="trolley" name="trolley" checked={editJobItem.trolley} onChange={(e) => handleTrolleyChange(e)}></input>
                            </label>
                        </div>

                        <div className={editJobItem.isKwikstage ? "hidden" : ""}>
                            <label className="input-group" htmlFor="editQuantityTotal">
                                <span className="label">Quantity Total</span>
                                <input type='number' min={0} step={1} className="input modal-inputsize200 textalignright" id="editQuantityTotal" name="quantityTotal" value={editJobItem.quantityTotal} onChange={(e) => handleQuantityTotalChange(e)} disabled={editJobItem.itemTypeId === 23 || (editJobItem.itemTypeId === 58 && editJobItem.item.includes("Septic")) || (!props.fromRunsheet && editJobItem.isAllocated)} />
                            </label>
                            <span className="label errors marginLeftLabel">{errors["editQuantityTotal"]}</span>

                            <label className="input-group" htmlFor="editQuantity">
                                <span className="label">Quantity Scheduled</span>
                                <input type='number' min={0} step={1} className="input modal-inputsize200 textalignright" id="editQuantity" name="quantityScheduled" value={editJobItem.quantityScheduled} onChange={(e) => handleChange(e)} disabled={editJobItem.itemTypeId === 23 || (editJobItem.itemTypeId === 58 && editJobItem.item.includes("Septic")) || (!props.fromRunsheet && editJobItem.isAllocated)} />
                            </label>
                            <span className="label errors marginLeftLabel">{errors["editquantity"]}</span>
                        </div>

                        <label className={editJobItem.itemTypeId === 14 || editJobItem.itemTypeId === 17 ? "input-group" : "hidden"} htmlFor="itemId">
                            <span className="label">Extra Type</span>
                            <div className="makeitflexspacebetween">
                                <select className="select jobschedule-newjob-extra--width" value={editJobItem.extras.itemId} disabled>
                                    <option hidden defaultValue="-1"></option>
                                    {extraItemOptions.map(item =>
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    )};
                                </select>
                                <input type="number" min="0" step="1" className="marginLeft10 jobschedule-qty--width" id="newitemquantity" name="newitemquantity" value={editJobItem.extras.quantity} onChange={(e) => handleExtrasQtyChange(e)} />
                            </div>

                            <label className="input-group">
                                <span className="label">Unit Includes</span>
                                <span>{
                                    (editJobItem.extras.quantity + editJobItem.tables) + (editJobItem.extras.itemId === 52 ? " tables, " : " desks, ")
                                    + (((editJobItem.extras.itemId === 52 ? 4 : 2) * editJobItem.extras.quantity) + editJobItem.chairs) + " chairs and air conditioning"}</span>
                            </label>
                        </label>
                        <label className={editJobItem.itemTypeId === 27 ? "input-group" : "hidden"} htmlFor="crossoversection">
                            <span className="label">Extra Section (0.3m)</span>
                            <input type="number" min="0" step="1" className="marginLeft10 jobschedule-qty--width" id="newitemquantity" name="newitemquantity" value={editJobItem.extras.quantity} onChange={(e) => handleCrossoverQtyChange(e)} disabled={!props.fromRunsheet && editJobItem.isAllocated} />
                        </label>
                        <div className={editJobItem.isKwikstage ? "" : "hidden"}>
                            <span className="makeitred">Note: Editing a Kwikstage item updates all associated items</span>
                            {editJobItem.scheduleKwikstageItems.map((item, index) =>
                                <div key={index}>
                                    <label className="label rollover-padding">
                                        {item.starterBayId}
                                    </label>
                                    <label className="label rollover-padding">
                                        {item.liftId}
                                    </label>
                                    <label className="label rollover-padding">
                                        {item.baysTotal} bays
                                    </label>
                                    <label className="input-group" htmlFor="baysTotal">
                                        <input type='number' min={0} step={1} className="input modal-inputsize200 textalignright" id="baysTotal" name="baysTotal" value={item.baysTotal} onChange={(e) => handleKwikstageChange(e, index)} />
                                    </label>
                                </div>
                            )}
                            <span className="label errors marginLeftLabel">{errors["baysTotal"]}</span>
                        </div>

                        <label className={editJobItem.itemTypeId === 26 ? "hidden" : "input-group"} htmlFor="initial">
                            <span className="label">Initial</span>
                            <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="initial" name="initial" value={editJobItem.initial} onChange={(e) => handlePriceChange(e)} disabled={(!props.fromRunsheet && editJobItem.isAllocated && editJobItem.itemTypeId != 61)} />
                        </label>
                        <span className="label errors marginLeftLabel">{errors["initial"]}</span>
                        <label className={editJobItem.itemTypeId === 26 ? "hidden" : "input-group"} htmlFor="travel">
                            <span className="label">Travel</span>
                            <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="travel" name="travel" value={editJobItem.travel} onChange={(e) => handlePriceChange(e)} disabled={(!props.fromRunsheet && editJobItem.isAllocated && editJobItem.itemTypeId != 61)} />
                        </label>
                        
                        <span className="label errors marginLeftLabel">{errors["travel"]}</span>
                        <div className={editJobItem.isEws || editJobItem.itemTypeId === 23 || editJobItem.productGroupId === 6 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="cleaningFee">
                                <span className="label">{editJobItem.itemTypeId === 23 ? "Connection" : editJobItem.productGroupId === 6 ? "Callout Fee" : "Cleaning Fee"}</span>
                                <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="cleaningFee" name="cleaningFee" value={editJobItem.cleaningFee} onChange={(e) => handlePriceChange(e)} disabled={(!props.fromRunsheet && editJobItem.isAllocated && editJobItem.itemTypeId != 61)} />
                            </label>
                            <span className="label errors marginLeftLabel">{errors["cleaningFee"]}</span>
                        </div>
                        <label className={editJobItem.itemTypeId === 23 || editJobItem.itemTypeId === 61 ? "input-group" : "hidden"} htmlFor="trenchFee">
                            <span className="label">{editJobItem.itemTypeId === 61 ? "Truck Hire" : "Trench Fee"}</span>
                            <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="trenchFee" name="trenchFee" value={editJobItem.trenchFee} onChange={(e) => handlePriceChange(e)} disabled={(!props.fromRunsheet && editJobItem.isAllocated && editJobItem.itemTypeId != 61)} />
                        </label>

                        <div className={editJobItem.itemTypeId === 61 ? "" : "hidden" }>
                            <label className="input-group" htmlFor="confinedSpaceAmount">
                                <span className="label">Confined Space</span>
                                <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="confinedSpaceAmount" name="confinedSpaceAmount" value={editJobItem.confinedSpaceAmount} onChange={(e) => handlePriceChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="sampleTestingAmount">
                                <span className="label">Sample Testing</span>
                                <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="sampleTestingAmount" name="sampleTestingAmount" value={editJobItem.sampleTestingAmount} onChange={(e) => handlePriceChange(e)} />
                            </label>
                            <label className="input-group" htmlFor="wasteDisposalAmount">
                                <span className="label">Waste Disposal</span>
                                <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="wasteDisposalAmount" name="wasteDisposalAmount" value={editJobItem.wasteDisposalAmount} onChange={(e) => handlePriceChange(e)} />
                            </label>
                        </div>
                        <label className="input-group" htmlFor="fuelSurchargeAmount">
                            <span className="label">Fuel Surcharge</span>
                            <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="fuelSurchargeAmount" name="fuelSurchargeAmount" value={editJobItem.fuelSurchargeAmount} onChange={(e) => handlePriceChange(e)} />
                        </label>

                        <label className="input-group" htmlFor="editPrice">
                            <span className="label">Price</span>
                            <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="editPrice" name="price" value={editJobItem.price} onChange={(e) => handleChange(e)} disabled={!props.fromRunsheet && editJobItem.isAllocated} />
                        </label>
                        <span className="label errors marginLeftLabel">{errors["editprice"]}</span>

                        <label className={editJobItem.itemTypeId === 22 || editJobItem.itemTypeId === 26 || editJobItem.itemTypeId === 44 || editJobItem.itemTypeId === 45 || editJobItem.itemTypeId === 47 || editJobItem.productGroupId === 6 ? "hidden" : "input-group"} htmlFor="editOverhireRate">
                            <span className="label">Overhire Rate</span>
                            <input type='number' min={0} step={0.01} className="input modal-inputsize200 textalignright" id="editOverhireRate" name="overhireRate" value={editJobItem.overhireRate} onChange={(e) => handleChange(e)} disabled={!editJobItem.canEditOverhire || (!props.fromRunsheet && editJobItem.isAllocated)} />
                        </label>
                        <span className="label errors marginLeftLabel">{errors["editoverhirerate"]}</span>

                        <div className={invoiceAmount > 0 ? "marginTop10 marginBottom10" : "hidden"}>
                            <div>NOTE: An Invoice will be created for the extra {invoicePcExtra} pump & cleans (for {editJobItem.quantityTotal} {editJobItem.quantityTotal === 1 ? "toilet" : "toilets"}) added within the initial hire period for {invoiceAmountFormatted}</div>
                            <div>NOTE: There is a total of {invoicePcRemaining} remaining p&c jobs</div>
                        </div>

                        <label className="input-group" htmlFor="editReason">
                            <span className="label-xlarge">Reason for change {editJobItem.itemTypeId === 22 && editJobItem.price === 0 ? "and reason why price is $0" : ""}</span>
                            <textarea className="input allocate__teamnotes" id="editReason" name="reason" value={editJobItem.reason} onChange={(e) => handleChange(e)}></textarea>
                        </label>
                        <span className="label errors">{errors["editreason"]}</span>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className={itemQuestions.showQuestions && itemQuestions.itemType === 1 ? "" : "hidden"}>
                    <button type="button" className="popup-button--yes" onClick={hideQuestions}>Next</button>
                </div>
                <div className={itemQuestions.showQuestions ? "hidden" : editJobItem.itemTypeId === 23 || editJobItem.productGroupId === 6 ? "makeitflexspacebetween mobile-width" : ""}>
                    <button type="button" className={editJobItem.itemTypeId === 23 || editJobItem.productGroupId === 6 ? "popup-button--yes" : "hidden"} onClick={() => { setItemQuestions({ showQuestions: true, itemType: editJobItem.productGroupId === 6 ? 2 : 1 }); deleteError("epaQuestions"); setSaveDisabled(false); }}>Questions</button>
                    <button className="popup-button--yes" onClick={save}>Save</button>
                </div>
                <div className={errors["epaQuestions"] ? "label errors" : "hidden"}>{errors["epaQuestions"]}</div>
            </Modal.Footer>
        </Modal>
    );

let contents = loading && props.showModal
    ? <p><em>Loading...</em></p> : loading ? ""
    : renderDetails;

return (
    <div>
        {contents}
    </div>
);

}

export default RunsheetEditJobPopup;